// src/components/WhyTrustUs.js — Light Theme
import React, { useState } from "react";
import styled from "styled-components";
import {
  FaChartLine,
  FaUserCircle,
  FaComments,
  FaCalendarCheck,
  FaDice,
  FaUserPlus,
  FaCheck,
  FaGift,
  FaChevronDown,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";

/* ---- MAIN COMPONENT ---- */
const WhyTrustUs = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <Wrapper>

      {/* SOCIAL SECTION */}
      <SectionHeader>
        <SectionBadge color="#29ABE2" bg="#e4f5fc">Community Platform</SectionBadge>
        <SectionTitle>Trend Genie Social</SectionTitle>
        <SectionSub>
          A crypto-focused social platform where traders connect, share insights, and stay
          updated on the best crypto projects and AI trading signals in real time.
        </SectionSub>
      </SectionHeader>

      <Grid>
        {[
          {
            icon: <FaChartLine />,
            title: "Discover What's Trending",
            desc: "Explore popular posts, new AI crypto signal updates, and fresh content so you never miss what's happening in the market.",
            color: "#29ABE2",
            bg: "#e4f5fc",
          },
          {
            icon: <FaUserCircle />,
            title: "Profile & Community Identity",
            desc: "Build your profile, grow your presence, and be part of a focused crypto community around the best crypto project ecosystem.",
            color: "#0ea5e9",
            bg: "#e0f2fe",
          },
          {
            icon: <FaComments />,
            title: "Social Feed & Networking",
            desc: "Stay informed with a centralized feed for updates, conversations, and AI trading signal discussions across the community.",
            color: "#F7941D",
            bg: "#fff4e5",
          },
        ].map((item, i) => (
          <LightCard
            key={i}
            as={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
          >
            <CardIconWrap color={item.color} bg={item.bg}>{item.icon}</CardIconWrap>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </LightCard>
        ))}
      </Grid>

      {/* CTA #1 — Social */}
      <CTABlock
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <CTALeft>
          <CTAIconBox color="#29ABE2" bg="#e4f5fc">
            <FaComments />
          </CTAIconBox>
        </CTALeft>
        <CTARight>
          <CTATitle>
            Your crypto community starts with{" "}
            <CTAHighlight>Trend Genie Social</CTAHighlight>
          </CTATitle>
          <CTASub>
            Post updates, discover trending AI crypto signals discussions, and connect with
            traders, builders, and creators inside the best crypto project social platform.
          </CTASub>
          <CheckList>
            <CheckItem><FaCheck /><span>Publish posts and engage through likes and comments in one clean feed</span></CheckItem>
            <CheckItem><FaCheck /><span>Build your profile and grow your presence inside the Trend Genie ecosystem</span></CheckItem>
            <CheckItem><FaCheck /><span>Explore what is trending without missing key community updates</span></CheckItem>
          </CheckList>
          <CTABtns>
            <PrimaryBtn
              href="https://social.trendgenie.io"
              target="_blank"
              rel="noopener noreferrer"
              as={motion.a}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Join Trend Genie Social
            </PrimaryBtn>
            <SecondaryBtn href="#faq" as={motion.a} whileHover={{ y: -2 }}>
              Learn More
            </SecondaryBtn>
          </CTABtns>
        </CTARight>
      </CTABlock>

      {/* AIRDROP SECTION */}
      <SectionHeader style={{ marginTop: "6rem" }}>
        <SectionBadge color="#f59e0b" bg="#fef3c7">Rewards Program</SectionBadge>
        <SectionTitle id="airdrop">Airdrop Program</SectionTitle>
        <SectionSub>
          Participate in the Trend Genie airdrop by completing simple tasks inside the
          best free crypto signals app and earn your share of $TGEN tokens.
        </SectionSub>
      </SectionHeader>

      <Grid>
        {[
          {
            icon: <FaCalendarCheck />,
            title: "1. Daily Check-Ins",
            desc: "Complete a quick daily check-in to build consistency and increase your airdrop eligibility over time.",
            color: "#f59e0b",
            bg: "#fef3c7",
          },
          {
            icon: <FaDice />,
            title: "2. Spins & Tasks",
            desc: "Participate through spins and simple in-app tasks that contribute toward your overall airdrop progress.",
            color: "#10b981",
            bg: "#d1fae5",
          },
          {
            icon: <FaUserPlus />,
            title: "3. Referrals",
            desc: "Invite others to join Trend Genie Social and improve your airdrop standing through verified referrals.",
            color: "#ec4899",
            bg: "#fce7f3",
          },
        ].map((item, i) => (
          <LightCard
            key={i}
            as={motion.div}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
          >
            <CardIconWrap color={item.color} bg={item.bg}>{item.icon}</CardIconWrap>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </LightCard>
        ))}
      </Grid>

      {/* WHO IS IT FOR */}
      <SectionHeader style={{ marginTop: "6rem" }}>
        <SectionBadge color="#F7941D" bg="#fff4e5">For Everyone</SectionBadge>
        <SectionTitle>Who Is Trend Genie Designed For?</SectionTitle>
        <SectionSub>
          Whether you are searching for a free crypto signals app, building in Web3, or just
          getting started with crypto — Trend Genie has tools designed for you.
        </SectionSub>
      </SectionHeader>

      <Grid cols={4}>
        {[
          {
            title: "Crypto Traders & Investors",
            desc: "Access AI crypto signals, live charts, and market analysis to make smarter trading decisions every day.",
          },
          {
            title: "Web3 Builders & Founders",
            desc: "Share progress, get feedback, and connect with early users, collaborators, and supporters in the ecosystem.",
          },
          {
            title: "Crypto Communities & Creators",
            desc: "Grow your presence, start conversations, and build a network around the best crypto project discussions.",
          },
          {
            title: "Newcomers Learning Crypto",
            desc: "A beginner-friendly free crypto app to learn the basics, follow discussions, and understand AI trading signals.",
          },
        ].map((item, i) => (
          <AudienceCard
            key={i}
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1, duration: 0.5 }}
          >
            <AudienceNumber>0{i + 1}</AudienceNumber>
            <CardTitle>{item.title}</CardTitle>
            <CardDesc>{item.desc}</CardDesc>
          </AudienceCard>
        ))}
      </Grid>

      {/* COMPARISON BARS */}
      <SectionHeader style={{ marginTop: "6rem" }}>
        <SectionBadge color="#0ea5e9" bg="#e0f2fe">Performance</SectionBadge>
        <SectionTitle>Trend Genie vs Traditional Trading</SectionTitle>
        <SectionSub>
          See why thousands of traders choose Trend Genie as their preferred AI crypto
          signals app over manual, emotion-driven trading.
        </SectionSub>
      </SectionHeader>

      <ComparisonGrid>
        {[
          { title: "Signal Speed", ai: 95, manual: 30, label: "AI reacts in milliseconds.", color: "#29ABE2" },
          { title: "Consistency", ai: 88, manual: 45, label: "No emotional biases.", color: "#0ea5e9" },
          { title: "Stress Level", ai: 15, manual: 85, label: "Minimal stress with AI.", color: "#10b981" },
        ].map((item, i) => (
          <CompareCard key={i}>
            <CompareTitle>{item.title}</CompareTitle>
            <CompareRow>
              <CompareLabel>Trend Genie AI</CompareLabel>
              <BarTrack>
                <BarFill
                  style={{ width: `${item.ai}%`, background: item.color }}
                  as={motion.div}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${item.ai}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
              </BarTrack>
              <BarPct style={{ color: item.color }}>{item.ai}%</BarPct>
            </CompareRow>
            <CompareRow>
              <CompareLabel>Manual Trading</CompareLabel>
              <BarTrack>
                <BarFill
                  style={{ width: `${item.manual}%`, background: "#e2e8f0" }}
                  as={motion.div}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${item.manual}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
                />
              </BarTrack>
              <BarPct>{item.manual}%</BarPct>
            </CompareRow>
            <CompareNote>{item.label}</CompareNote>
          </CompareCard>
        ))}
      </ComparisonGrid>

      {/* CTA #2 — Airdrop */}
      <CTABlock
        style={{ marginTop: "4rem" }}
        as={motion.div}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
      >
        <CTALeft>
          <CTAIconBox color="#f59e0b" bg="#fef3c7">
            <FaGift />
          </CTAIconBox>
        </CTALeft>
        <CTARight>
          <CTATitle>
            Early participation gets{" "}
            <CTAHighlight>recognized</CTAHighlight>
          </CTATitle>
          <CTASub>
            The airdrop program is designed to recognize early activity inside Trend Genie
            Social — the best free crypto signals app — through selected in-app actions and
            official updates.
          </CTASub>
          <CheckList>
            <CheckItem><FaCheck /><span>Build progress through check-ins, spins, and eligible in-app tasks</span></CheckItem>
            <CheckItem><FaCheck /><span>Improve standing through verified referrals and real participation</span></CheckItem>
            <CheckItem><FaCheck /><span>Rely on official announcements for rules, eligibility, and distribution timing</span></CheckItem>
          </CheckList>
          <CTABtns>
            <PrimaryBtn
              href="https://social.trendgenie.io"
              target="_blank"
              rel="noopener noreferrer"
              as={motion.a}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Start Participating
            </PrimaryBtn>
            <SecondaryBtn href="#airdrop" as={motion.a} whileHover={{ y: -2 }}>
              View Details
            </SecondaryBtn>
          </CTABtns>
        </CTARight>
      </CTABlock>

      {/* FAQ */}
      <SectionHeader style={{ marginTop: "6rem" }}>
        <SectionBadge color="#29ABE2" bg="#e4f5fc">FAQ</SectionBadge>
        <SectionTitle id="faq">Frequently Asked Questions</SectionTitle>
        <SectionSub>
          Everything you need to know about the best AI crypto signals app, free crypto
          signals, and the Trend Genie ecosystem.
        </SectionSub>
      </SectionHeader>

      <FAQList>
        {[
          {
            q: "Is Trend Genie a free crypto signals app?",
            a: "Yes. Trend Genie offers a 7-day free trial with full access to AI crypto signals, charts, and trade alerts. It is one of the best free crypto signals apps available on Android.",
          },
          {
            q: "What makes Trend Genie the best crypto project?",
            a: "Trend Genie combines a free AI crypto signals app, DeFi safety scanner (RugShield), a Web3 social platform, and the $TGEN utility token — making it one of the most complete crypto ecosystems available.",
          },
          {
            q: "How does the AI crypto signals app work?",
            a: "Trend Genie scans live markets using AI models to detect patterns and generate signals with entry, stop-loss, and take-profit levels automatically. Signals update in real time on your Android device.",
          },
          {
            q: "What is Trend Genie Social?",
            a: "Trend Genie Social is a crypto-focused social platform where users can post updates, engage with community content, and stay connected with what's trending across the best crypto project ecosystem.",
          },
          {
            q: "What is the Trend Genie airdrop?",
            a: "The airdrop is a community program designed to recognize early participation inside Trend Genie Social through selected in-app activities including check-ins, tasks, spins, and referrals.",
          },
          {
            q: "When will the airdrop be distributed?",
            a: "Distribution timing and eligibility rules are announced through official Trend Genie channels. Please rely on in-app updates and official announcements only.",
          },
        ].map((faq, i) => (
          <FAQItem key={i}>
            <FAQQuestion onClick={() => toggleFaq(i)}>
              <span>{faq.q}</span>
              <FAQChevron open={openFaq === i}><FaChevronDown /></FAQChevron>
            </FAQQuestion>
            <AnimatePresence>
              {openFaq === i && (
                <FAQAnswer
                  as={motion.div}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <FAQAnswerInner>{faq.a}</FAQAnswerInner>
                </FAQAnswer>
              )}
            </AnimatePresence>
          </FAQItem>
        ))}
      </FAQList>

    </Wrapper>
  );
};

export default WhyTrustUs;

/* ---- STYLES ---- */

const Wrapper = styled.section`
  padding: 5rem 8%;
  text-align: center;
  background: var(--bg-main);
`;

const SectionHeader = styled.div`
  max-width: 700px;
  margin: 0 auto 3rem;
`;

const SectionBadge = styled.span`
  display: inline-block;
  background: ${p => p.bg};
  color: ${p => p.color};
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 0.35rem 0.9rem;
  border-radius: 999px;
  margin-bottom: 0.9rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-color);
  margin-bottom: 0.9rem;
  letter-spacing: -0.5px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSub = styled.p`
  color: var(--text-secondary-color);
  font-size: 1.02rem;
  line-height: 1.75;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const LightCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 2rem 1.75rem;
  text-align: left;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: var(--shadow-lg);
    border-color: transparent;
  }
`;

const CardIconWrap = styled.div`
  width: 46px;
  height: 46px;
  border-radius: 13px;
  background: ${p => p.bg};
  color: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  margin-bottom: 1.1rem;
`;

const CardTitle = styled.h3`
  color: var(--text-color);
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.6rem;
  line-height: 1.3;
`;

const CardDesc = styled.p`
  color: var(--text-secondary-color);
  font-size: 0.95rem;
  line-height: 1.7;
`;

/* CTA Block */
const CTABlock = styled.div`
  display: flex;
  gap: 3rem;
  align-items: center;
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: 24px;
  padding: 3rem;
  margin-top: 3rem;
  text-align: left;
  max-width: 1100px;
  margin-left: auto;
  margin-right: auto;
  box-shadow: var(--shadow-md);

  @media (max-width: 900px) {
    flex-direction: column;
    padding: 2rem 1.5rem;
    gap: 2rem;
    text-align: left;
  }
`;

const CTALeft = styled.div`
  flex-shrink: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

const CTAIconBox = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 28px;
  background: ${p => p.bg};
  color: ${p => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.8rem;
`;

const CTARight = styled.div`
  flex: 1;
`;

const CTATitle = styled.h3`
  font-size: 2.2rem;
  font-weight: 800;
  color: var(--text-color);
  line-height: 1.2;
  margin-bottom: 0.9rem;
  letter-spacing: -0.3px;

  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const CTAHighlight = styled.span`
  background: linear-gradient(135deg, #29ABE2, #F7941D);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const CTASub = styled.p`
  color: var(--text-secondary-color);
  font-size: 1.02rem;
  line-height: 1.75;
  margin-bottom: 1.5rem;
`;

const CheckList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CheckItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.7rem;
  font-size: 0.97rem;
  color: var(--text-secondary-color);
  line-height: 1.55;

  svg {
    color: var(--success-color);
    flex-shrink: 0;
    margin-top: 3px;
    font-size: 0.85rem;
  }
`;

const CTABtns = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.9rem 1.8rem;
  border-radius: 14px;
  background: linear-gradient(135deg, #29ABE2, #1A5276);
  color: white;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  box-shadow: var(--shadow-primary);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-primary-lg);
  }
`;

const SecondaryBtn = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 0.9rem 1.6rem;
  border-radius: 14px;
  background: var(--bg-surface);
  color: var(--text-secondary-color);
  border: 1.5px solid var(--border-color);
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

/* Audience Cards */
const AudienceCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 1.8rem 1.5rem;
  text-align: left;
  box-shadow: var(--shadow-sm);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-md);
    border-color: transparent;
  }
`;

const AudienceNumber = styled.div`
  font-size: 0.72rem;
  font-weight: 800;
  color: var(--primary-color);
  letter-spacing: 0.05em;
  margin-bottom: 0.7rem;
  background: var(--primary-light);
  display: inline-block;
  padding: 0.25rem 0.6rem;
  border-radius: 6px;
`;

/* Comparison */
const ComparisonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  max-width: 1100px;
  margin: 0 auto;
`;

const CompareCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 2rem 1.75rem;
  text-align: left;
  box-shadow: var(--shadow-sm);
`;

const CompareTitle = styled.h4`
  color: var(--text-color);
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 1.2rem;
`;

const CompareRow = styled.div`
  margin-bottom: 1rem;
`;

const CompareLabel = styled.div`
  font-size: 0.8rem;
  color: var(--text-secondary-color);
  font-weight: 500;
  margin-bottom: 0.4rem;
`;

const BarTrack = styled.div`
  height: 8px;
  background: var(--bg-tertiary);
  border-radius: 10px;
  overflow: hidden;
  display: inline-block;
  width: calc(100% - 44px);
  vertical-align: middle;
`;

const BarFill = styled.div`
  height: 100%;
  border-radius: 10px;
`;

const BarPct = styled.span`
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-secondary-color);
  margin-left: 8px;
  vertical-align: middle;
`;

const CompareNote = styled.p`
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0;
`;

/* FAQ */
const FAQList = styled.div`
  max-width: 780px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FAQItem = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border-color);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: var(--shadow-sm);
  text-align: left;
`;

const FAQQuestion = styled.button`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.2rem 1.5rem;
  background: none;
  border: none;
  cursor: pointer;
  gap: 1rem;
  text-align: left;

  span {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
    line-height: 1.4;
  }
`;

const FAQChevron = styled.div`
  color: var(--text-muted);
  flex-shrink: 0;
  transform: ${p => (p.open ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
  font-size: 0.85rem;
`;

const FAQAnswer = styled.div`
  overflow: hidden;
`;

const FAQAnswerInner = styled.p`
  padding: 0 1.5rem 1.2rem;
  color: var(--text-secondary-color);
  font-size: 0.95rem;
  line-height: 1.7;
  margin: 0;
`;
